import { create } from "zustand";
import { InboxMessage, Attachment } from "@/types/types";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";

interface InboxState {
  inbox: InboxMessage[];
  activeCreatorName: string | null;
  isLoading: boolean;
  loadingManualSend: boolean;
  error: string | null;
  /** opts.isEve: Amy-run campaign — read the thread from Amy's pinned mailbox first (see fetchThreadMessages). */
  fetchInbox: (campaignCreatorId: string, token: string, opts?: { isEve?: boolean }) => Promise<any>;
  /** opts.isEve: Amy-run campaign — send from Amy's pinned mailbox, where the thread lives. Throws when the send fails. */
  manualSend: (campaignCreatorId: string, emailContent: string, emailSubject: string, token: string, translate?: boolean, opts?: { isEve?: boolean }) => Promise<any>;
  deleteNextMessage: (campaignCreatorId: string, token: string) => Promise<boolean>;
  updateNextMessage: (campaignCreatorId: string, editedNextMessage: string, token: string) => Promise<boolean>;
  sendNextMessage: (campaignCreatorId: string, token: string) => Promise<boolean>;
  fetchAttachment: (attachment: Attachment, messageId: string, campaignCreatorId: string, token: string) => Promise<Blob | null>;
  setActiveCreatorName: (name: string) => void;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

/**
 * Load a creator's email thread. Amy-run campaigns read it from the
 * campaign's pinned Composio mailbox via amy's get-creator-thread route: the
 * Flask getThread reads the campaign OWNER's Clerk Gmail, which 404s every
 * Amy thread unless the owner happens to have that same Gmail connected
 * (2026-09-24). Any amy failure — thread not in the pinned mailbox (a creator
 * the legacy engine emailed from the owner's Gmail), amy unreachable — falls
 * back to Flask, so nothing regresses. Same message shape either way.
 */
export async function fetchThreadMessages(
  campaignCreatorId: string,
  token: string,
  opts: { isEve?: boolean } = {}
): Promise<InboxMessage[]> {
  if (opts.isEve) {
    try {
      const response = await fetch(`${EVE_HOST}/eve/v1/actions/get-creator-thread`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
      });
      const data = (await response.json()) as { ok?: boolean; messages?: InboxMessage[] };
      if (data.ok && Array.isArray(data.messages)) {
        return data.messages.map((m) => ({
          ...m,
          sent_at_iso: m.sent_at,
          // Flask's thread already renders sent_at relative ("3 hours ago")
          // and the inbox prints it verbatim; Amy's route sends ISO.
          sent_at: timeAgo(m.sent_at),
          // fetchAttachment reads these back from the same mailbox.
          attachments: m.attachments?.map((a) => ({ ...a, via_amy: true })),
        }));
      }
    } catch {
      // Fall through to the legacy route.
    }
  }

  const response = await fetch(`${API_BASE_URL}/api/campaigns/getThread`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
  });

  if (!response.ok) throw new Error("Failed to fetch inbox");

  const data = await response.json();
  return Array.isArray(data.data) ? data.data : [];
}

export const useInboxStore = create<InboxState>((set) => ({
  inbox: [],
  activeCreatorName: null,
  isLoading: false,
  loadingManualSend: false,
  error: null,

  fetchInbox: async (campaignCreatorId: string, token: string, opts?: { isEve?: boolean }) => {
    set({ isLoading: true, error: null, inbox: [] });
    try {
      const messages = await fetchThreadMessages(campaignCreatorId, token, opts);
      set({ inbox: messages, isLoading: false });
      return messages;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return [];
    }
  },

  manualSend: async (campaignCreatorId: string, emailContent: string, emailSubject: string, token: string, translate: boolean = false, opts: { isEve?: boolean } = {}) => {
    set({ loadingManualSend: true, error: null });
    try {
      // Amy-run campaign: the thread lives in the campaign's pinned Composio
      // mailbox, so the reply is sent from there via amy's send-creator-reply
      // route — Flask sends through the campaign OWNER's Clerk Gmail, which
      // fails once that grant lapses or is another mailbox (2026-10-04). Only
      // a thread the pinned mailbox does not hold (refused BEFORE any send)
      // falls back to Flask; every other failure surfaces, never a second
      // send. "Translate & Send" stays on Flask, which owns the translation.
      if (opts.isEve && !translate) {
        const response = await fetch(`${EVE_HOST}/eve/v1/actions/send-creator-reply`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ campaign_creator_id: campaignCreatorId, email_content: emailContent }),
        });
        const data = (await response.json().catch(() => ({}))) as { ok?: boolean; code?: string; error?: string };
        if (data.ok) {
          set({ loadingManualSend: false });
          return true;
        }
        if (data.code !== "thread_not_found") throw new Error(data.error || "Failed to send email");
      }

      const response = await fetch(`${API_BASE_URL}/api/campaigns/manualSend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_creator_id: campaignCreatorId,
          email_content: emailContent,
          email_subject: emailSubject,
          translate: translate
        }),
      });

      if (!response.ok) throw new Error("Failed to send email");

      const data = await response.json();
      set({ loadingManualSend: false });
      return data.data;
    } catch (error) {
      // Rethrown so the caller does not report success, refetch and discard
      // the typed reply after a failed send.
      set({ error: (error as Error).message, loadingManualSend: false });
      throw error;
    }
  },

  fetchAttachment: async (attachment: Attachment, messageId: string, campaignCreatorId: string, token: string) => {
    const blobType = attachment.file_type || "application/octet-stream";
    // An attachment of a thread read from Amy's pinned mailbox is fetched
    // from there via amy's get-creator-attachment route: the Flask route
    // reads the campaign OWNER's Clerk Gmail, which does not hold Amy's
    // threads. Any amy failure falls back to Flask, same as the thread read.
    if (attachment.via_amy) {
      try {
        const response = await fetch(`${EVE_HOST}/eve/v1/actions/get-creator-attachment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: campaignCreatorId,
            message_id: messageId,
            attachment_id: attachment.attachment_id,
            file_name: attachment.file_name,
          }),
        });
        if (response.ok) return new Blob([await response.arrayBuffer()], { type: blobType });
      } catch {
        // Fall through to the legacy route.
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/getAttachment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_creator_id: campaignCreatorId,
          message_id: messageId,
          attachment_id: attachment.attachment_id,
        }),
      });

      if (!response.ok) throw new Error("Failed to load attachment");

      return new Blob([await response.arrayBuffer()], { type: blobType });
    } catch (error) {
      console.error("Error loading attachment:", error);
      toast.error("Failed to load attachment");
      return null;
    }
  },

  deleteNextMessage: async (campaignCreatorId: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/deleteNextMessage`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
      });

      if (!response.ok) {
        toast.error("Failed to delete next message");
        throw new Error("Failed to delete next message");
      }
      return true;
    } catch (error) {
      console.error("Error deleting next message:", error);
      toast.error("Failed to delete next message");
      return false;
    }
  },

  updateNextMessage: async (campaignCreatorId: string, editedNextMessage: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/updateNextMessage`, {
        method: "PUT",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId, edited_next_message: editedNextMessage }),
      });

      if (!response.ok) {
        toast.error("Failed to update next message");
        throw new Error("Failed to update next message");
      }
      return true;
    } catch (error) {
      console.error("Error updating next message:", error);
      toast.error("Failed to update next message");
      return false;
    }
  },

  sendNextMessage: async (campaignCreatorId: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/sendNextMessage`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
      });

      if (!response.ok) {
        toast.error("Failed to send next message");
        throw new Error("Failed to send next message");
      }
      return true;
    } catch (error) {
      console.error("Error sending next message:", error);
      toast.error("Failed to send next message");
      return false;
    }
  },

  setActiveCreatorName: (name: string) => set({ activeCreatorName: name }),

  reset: () =>
    set({ inbox: [], activeCreatorName: null, isLoading: false, error: null }),
}));
